
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaShieldAlt, FaTimesCircle, FaSignOutAlt, FaChevronDown, FaChevronRight, FaCheckCircle } from "react-icons/fa";
import api from "../api/axios";
import Navbar from "./Navbar";
import EmployerSidebar, { ChevronDownIcon } from "./EmployerSidebar";

const COLORS = { blue: "#9333EA", orange: "#F59E0B", dark: "#1E1033" };

const STATUS_STYLES = {
  active: { bg: "#F0FDF4", text: "#16A34A" },
  cancelled: { bg: "#FEF2F2", text: "#DC2626" },
  exited: { bg: "#F3F4F6", text: "#6B7280" },
};

const RETENTION_STATUS_STYLES = {
  pending: { bg: "#FFFBEB", text: "#B45309" },
  confirmed: { bg: "#F0FDF4", text: "#16A34A" },
  skipped: { bg: "#F3F4F6", text: "#6B7280" },
};

export default function EmployerPlacements() {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });

  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [exitModal, setExitModal] = useState(null);
  const [exitReason, setExitReason] = useState("");

  // Mobile sidebar drawer toggle (same pattern as EmployerReports)
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  // Retention state — keyed by placement_id
  const [expandedPlacementId, setExpandedPlacementId] = useState(null);
  const [retentionsByPlacement, setRetentionsByPlacement] = useState({});
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [confirmingRetentionId, setConfirmingRetentionId] = useState(null);

  const loadPlacements = () => {
    setLoading(true);
    api
      .get("/placements", { params: statusFilter ? { status: statusFilter } : {} })
      .then((res) => setPlacements(res.data.data ?? res.data))
      .catch(() => setError("Couldn't load placements."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
    loadPlacements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const verify = async (id) => {
    setBusyId(id);
    try {
      await api.put(`/placements/${id}/verify`);
      loadPlacements();
    } catch {
      setError("Couldn't verify this placement.");
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this placement? Pending retention milestones will be skipped.")) return;
    setBusyId(id);
    try {
      await api.put(`/placements/${id}/cancel`);
      loadPlacements();
    } catch {
      setError("Couldn't cancel this placement.");
    } finally {
      setBusyId(null);
    }
  };

  const submitExit = async () => {
    if (!exitReason.trim()) return;
    setBusyId(exitModal.id);
    try {
      await api.put(`/placements/${exitModal.id}/exit`, { exit_reason: exitReason.trim() });
      setExitModal(null);
      setExitReason("");
      loadPlacements();
    } catch {
      setError("Couldn't mark this placement as exited.");
    } finally {
      setBusyId(null);
    }
  };

  // Fetch retention milestones for one placement, only on first expand —
  // /placement-retentions doesn't filter by placement server-side per the
  // routes we have, so we fetch this employer's full list once and cache it
  // client-side, keyed by placement_id.
  const toggleExpand = async (placementId) => {
    if (expandedPlacementId === placementId) {
      setExpandedPlacementId(null);
      return;
    }
    setExpandedPlacementId(placementId);

    if (retentionsByPlacement[placementId]) return; // already cached

    setRetentionLoading(true);
    try {
      const res = await api.get("/placement-retentions");
      const all = res.data.data ?? res.data;
      const grouped = {};
      all.forEach((r) => {
        const pid = r.placement_id ?? r.placement?.id;
        if (!grouped[pid]) grouped[pid] = [];
        grouped[pid].push(r);
      });
      setRetentionsByPlacement((prev) => ({ ...prev, ...grouped }));
    } catch {
      setError("Couldn't load retention milestones.");
    } finally {
      setRetentionLoading(false);
    }
  };

  const confirmRetention = async (retentionId, placementId) => {
    setConfirmingRetentionId(retentionId);
    try {
      const res = await api.put(`/placement-retentions/${retentionId}/confirm`);
      setRetentionsByPlacement((prev) => ({
        ...prev,
        [placementId]: prev[placementId].map((r) =>
          r.id === retentionId ? { ...r, ...res.data } : r
        ),
      }));
    } catch {
      setError("Couldn't confirm this retention milestone.");
    } finally {
      setConfirmingRetentionId(null);
    }
  };

  const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
  const formatFee = (v) => (v == null ? "Not set" : "₹" + Number(v).toLocaleString("en-IN"));

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="flex">
        <EmployerSidebar user={user} activeTab="placements" />

        <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="mx-auto max-w-5xl px-4 py-6">
            {/* Mobile-only: nav drawer */}
            <button
              onClick={() => setShowFiltersMobile((s) => !s)}
              className="lg:hidden w-full mb-4 bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#1E1033]"
            >
              <span>☰ Menu</span>
              <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                {showFiltersMobile ? "Hide" : "Show"}
                <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? "rotate-180" : ""}`} />
              </span>
            </button>

            {showFiltersMobile && <EmployerSidebar user={user} activeTab="placements" mobile />}

            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#EC4899] bg-clip-text text-transparent inline-block">
                  Placements
                </h1>
                <p className="text-sm text-gray-500">Track confirmed joinings through to retention.</p>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
              >
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="exited">Exited</option>
              </select>
            </div>

            {error && (
              <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
            )}

            {loading ? (
              <div className="py-16 text-center text-gray-400">Loading placements...</div>
            ) : placements.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 py-16 text-center text-gray-400">
                No placements yet — these appear once a scheduled joining is confirmed.
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3 w-8"></th>
                      <th className="px-4 py-3">Candidate</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Joined</th>
                      <th className="px-4 py-3">Placement fee</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {placements.map((p) => {
                      const style = STATUS_STYLES[p.status] || STATUS_STYLES.active;
                      const isBusy = busyId === p.id;
                      const isExpanded = expandedPlacementId === p.id;
                      const retentions = retentionsByPlacement[p.id] || [];

                      return (
                        <>
                          <tr key={p.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <button
                                onClick={() => toggleExpand(p.id)}
                                className="text-gray-400 hover:text-gray-600"
                                title="Show retention milestones"
                              >
                                {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                              </button>
                            </td>
                            <td className="px-4 py-3 font-medium" style={{ color: COLORS.dark }}>
                              {p.candidate?.name ?? "—"}
                            </td>
                            <td className="px-4 py-3 text-gray-600">{p.job_posting?.title ?? "—"}</td>
                            <td className="px-4 py-3 text-gray-600">{formatDate(p.joining_date)}</td>
                            <td className="px-4 py-3 text-gray-600">
                              {formatFee(p.placement_fee)}
                              {p.verified_at && (
                                <span className="ml-1.5 text-xs font-medium" style={{ color: "#16A34A" }}>
                                  ✓ verified
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className="rounded-full px-2 py-1 text-xs font-medium capitalize"
                                style={{ backgroundColor: style.bg, color: style.text }}
                              >
                                {p.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-3 text-sm font-medium">
                                {p.status === "active" && !p.verified_at && (
                                  <button
                                    disabled={isBusy}
                                    onClick={() => verify(p.id)}
                                    className="flex items-center gap-1"
                                    style={{ color: COLORS.blue }}
                                    title="Verify placement fee"
                                  >
                                    <FaShieldAlt /> Verify
                                  </button>
                                )}
                                {p.status === "active" && (
                                  <>
                                    <button
                                      disabled={isBusy}
                                      onClick={() => setExitModal(p)}
                                      className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
                                      title="Mark exited"
                                    >
                                      <FaSignOutAlt /> Exit
                                    </button>
                                    <button
                                      disabled={isBusy}
                                      onClick={() => cancel(p.id)}
                                      className="flex items-center gap-1"
                                      style={{ color: "#DC2626" }}
                                      title="Cancel placement"
                                    >
                                      <FaTimesCircle /> Cancel
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr key={`${p.id}-retention`}>
                              <td colSpan={7} className="bg-gray-50 px-4 py-4">
                                {retentionLoading && !retentionsByPlacement[p.id] ? (
                                  <p className="text-sm text-gray-400">Loading milestones…</p>
                                ) : retentions.length === 0 ? (
                                  <p className="text-sm text-gray-400">
                                    No retention milestones recorded yet for this placement.
                                  </p>
                                ) : (
                                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                    {retentions
                                      .sort((a, b) => a.milestone_days - b.milestone_days)
                                      .map((r) => {
                                        const rStyle = RETENTION_STATUS_STYLES[r.status] || RETENTION_STATUS_STYLES.pending;
                                        return (
                                          <div
                                            key={r.id}
                                            className="rounded-lg border border-gray-200 bg-white px-3 py-2.5"
                                          >
                                            <div className="flex items-center justify-between">
                                              <span className="text-sm font-semibold" style={{ color: COLORS.dark }}>
                                                {r.milestone_days} days
                                              </span>
                                              <span
                                                className="rounded-full px-2 py-0.5 text-[11px] font-medium capitalize"
                                                style={{ backgroundColor: rStyle.bg, color: rStyle.text }}
                                              >
                                                {r.status}
                                              </span>
                                            </div>
                                            <p className="mt-1 text-xs text-gray-500">
                                              Due {formatDate(r.due_date)}
                                            </p>
                                            {r.status === "pending" && (
                                              <button
                                                onClick={() => confirmRetention(r.id, p.id)}
                                                disabled={confirmingRetentionId === r.id}
                                                className="mt-2 flex items-center gap-1 text-xs font-medium disabled:opacity-50"
                                                style={{ color: "#16A34A" }}
                                              >
                                                <FaCheckCircle size={11} />
                                                {confirmingRetentionId === r.id ? "Confirming…" : "Confirm"}
                                              </button>
                                            )}
                                            {r.status === "confirmed" && r.confirmed_at && (
                                              <p className="mt-1.5 text-[11px] text-gray-400">
                                                Confirmed {formatDate(r.confirmed_at)}
                                              </p>
                                            )}
                                          </div>
                                        );
                                      })}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {exitModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onMouseDown={(e) => e.target === e.currentTarget && setExitModal(null)}
        >
          <div className="w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="px-6 py-4 bg-gradient-to-r from-[#7C3AED] to-[#DB2777]">
              <h2 className="text-base font-semibold text-white">Mark placement exited</h2>
            </div>
            <div className="px-6 py-5">
              <p className="mb-3 text-sm text-gray-500">
                {exitModal.candidate?.name} — this closes the placement and skips any pending
                retention milestones.
              </p>
              <label className="mb-1.5 block text-sm font-medium text-gray-700">
                Exit reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={exitReason}
                onChange={(e) => setExitReason(e.target.value)}
                rows={3}
                maxLength={255}
                placeholder="e.g. Resigned, terminated for performance, role eliminated…"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
              />
              <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setExitModal(null);
                    setExitReason("");
                  }}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitExit}
                  disabled={!exitReason.trim() || busyId === exitModal.id}
                  className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60 bg-gradient-to-r from-[#7C3AED] to-[#DB2777] hover:opacity-90"
                >
                  {busyId === exitModal.id ? "Saving…" : "Confirm exit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}